import {
  isMemberAdmin as isMemberWorkspaceAdmin,
  getMembershipType as getWorkspaceMemberType,
} from '@trello/organizations';
import { ProductFeatures } from '@trello/product-features';
import type { Board, Member, BoardMembership } from './types';

type MemberType =
  | 'admin'
  | 'normal'
  | 'observer'
  | 'virtual'
  | 'deactivated'
  | 'unconfirmed'
  | 'org'
  | 'public';

type OrNull<T> = { [K in keyof T]?: T[K] | null };

export const getMembershipFor = (
  board: OrNull<Pick<Board, 'memberships'>>,
  memberId: string,
): BoardMembership | undefined => {
  // There are cases where `memberships` are returning null
  // and are not caught by typescript
  // TODO investigate how board.memberships can be null and why
  // typescript is not catching these errors
  if (!board.memberships) {
    return;
  }
  return board.memberships.find(
    (membership) => membership.idMember === memberId,
  );
};

// Disable implicitAdmin if you don't want enterprise admins and workspace
// admins to be automatically counted as board admins if workspace is premium or
// enterprise
export const getMemberType = (
  member: Pick<
    Member,
    'id' | 'memberType' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin'
  >,
  board: Pick<Board, 'memberships' | 'organization'>,
  implicitAdmin = true,
): MemberType => {
  if (member.memberType === 'ghost') {
    return 'virtual';
  }

  const workspace = board.organization;

  // If the member is an admin of the board's workspace's enterprise, they are
  // implicitly an admin of the board
  if (implicitAdmin && workspace?.enterprise?.idAdmins?.includes(member.id)) {
    return 'admin';
  }

  // If the member is an admin of the board's workspace, they are implicitly an
  // admin of the board if the workspace is premium
  if (
    implicitAdmin &&
    workspace &&
    ProductFeatures.hasProduct(workspace.products?.[0]) &&
    isMemberWorkspaceAdmin(member, workspace)
  ) {
    return 'admin';
  }

  const boardMembership = getMembershipFor(board, member.id);

  if (boardMembership) {
    if (boardMembership.deactivated) {
      return 'deactivated';
    }

    if (boardMembership.unconfirmed) {
      return 'unconfirmed';
    }

    return boardMembership.memberType;
  }

  // Converted from app/scripts/models/internal/membership-model.js getExplicitMemberType
  if (workspace && getWorkspaceMemberType(member, workspace)) {
    return 'org';
  }

  return 'public';
};

export const isMemberType = (
  member: Pick<
    Member,
    'id' | 'memberType' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin'
  >,
  board: Pick<Board, 'memberships' | 'organization'>,
  memberType: MemberType,
  implicitAdmin = true,
) => {
  return getMemberType(member, board, implicitAdmin) === memberType;
};

export const isMemberAdmin = (
  member: Pick<
    Member,
    'id' | 'memberType' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin'
  >,
  board: Pick<Board, 'memberships' | 'organization'>,
  implicitAdmin = true,
) => {
  return isMemberType(member, board, 'admin', implicitAdmin);
};

// Converted from app/scripts/lib/util.js
// Only does standard mode so far; add modes as you need
export const compareMemberTypes = (a: MemberType, b: MemberType) => {
  const hierarchy = [['org'], ['normal'], ['admin']];
  const aTier = hierarchy.findIndex((tier) => tier.includes(a ?? ''));
  const bTier = hierarchy.findIndex((tier) => tier.includes(b ?? ''));
  return aTier - bTier;
};

export const isMember = (
  board: { memberships?: { idMember: string }[] | null },
  idMember: string,
) => {
  // There are cases where `memberships` are returning null
  // and are not caught by typescript
  // TODO investigate how board.memberships can be null and why
  // typescript is not catching these errors
  if (!board.memberships) {
    return false;
  }

  return board.memberships.some(
    (membership) => membership.idMember === idMember,
  );
};
